<script>
	import { DatePicker } from '@svelte-plugins/datepicker';
	import { attributionOrderTable } from '../utils/reports-api';
	import { onMount } from 'svelte';

	const today = new Date();
	const MILLISECONDS_IN_DAY = 24 * 60 * 60 * 1000;

	let startDate = new Date(Date.now() - 7 * MILLISECONDS_IN_DAY);
	let endDate = today;

	let isOpen = false;
	let currentPage = 1;
	let tableData = [];
	let loading = false;
	let moreData = true;
	let orderID = '';
	let attribution = 'all';

	function formatDate(date) {
		try {
			return new Date(date).toLocaleDateString('en-US', {
				year: 'numeric',
				month: 'short',
				day: 'numeric'
			});
		} catch (error) {
			console.error('Error formatting date:', error);
			return 'Error';
		}
	}

	function formatDateForAPI(date) {
		try {
			const adjustedDate = new Date(date);
			const timezoneOffset = adjustedDate.getTimezoneOffset() * 60000; // Convert minutes to milliseconds
			const localDate = new Date(adjustedDate.getTime() - timezoneOffset);
			return localDate.toISOString().split('T')[0];
		} catch (error) {
			console.error('Error formatting date for API:', error);
			return 'Error';
		}
	}

	$: formattedStartDate = formatDate(startDate);
	$: formattedEndDate = formatDate(endDate);

	const toggleDatePicker = () => (isOpen = !isOpen);

	const onClearDates = () => {
		startDate = new Date(Date.now() - 7 * MILLISECONDS_IN_DAY);
		endDate = new Date();
	};
	$: if (startDate || endDate || attribution || orderID) {
		console.log('Dates changed, resetting and loading new data');
		loading = false;
		tableData = [];
		currentPage = 1;
		moreData = true;
		loadMoreData();
	}
	async function loadMoreData() {
		if (loading) return;
		loading = true;
		try {
			const response = await attributionOrderTable(
				formatDateForAPI(startDate),
				formatDateForAPI(endDate),
				null,
				null,
				currentPage,
				attribution,
				orderID
			);
			if (response.length === 0) {
				moreData = false;
			}
			tableData = [...tableData, ...response];
			currentPage++;
		} catch (error) {
			moreData = false;
			console.error('Error loading data:', error);
		} finally {
			loading = false;
		}
	}

	function handleScroll(e) {
		const bottom = e.target.scrollHeight - e.target.scrollTop === e.target.clientHeight;
		if (bottom) {
			loadMoreData();
		}
	}

	function handleSearch(event) {
		orderID = event.target.value;
	}
	async function exportToCSV() {
		if (!tableData.length) return;

		const data = await attributionOrderTable(
			formatDateForAPI(startDate),
			formatDateForAPI(endDate),
			null,
			null,
			1,
			attribution,
			orderID,
			999
		);
		console.log(data);

		const headers = Object.keys(data[0]);
		const csvRows = [
			headers.join(','),
			...data.map((row) =>
				headers.map((header) => `"${(row[header] ?? '').toString().replace(/"/g, '""')}"`).join(',')
			)
		];

		const csvContent = csvRows.join('\n');
		const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
		const start = formatDateForAPI(startDate);
		const end = formatDateForAPI(endDate);
		const filename = `orders_report_${start}_to_${end}.csv`;

		const link = document.createElement('a');
		link.setAttribute('href', URL.createObjectURL(blob));
		link.setAttribute('download', filename);
		document.body.appendChild(link);
		link.click();
		document.body.removeChild(link);
	}

	onMount(() => {
		loadMoreData();
	});
</script>

<section class="bordered-section">
	<h2>Orders Report</h2>
	<nav>
		<input
			type="text"
			placeholder="Search by order ID..."
			bind:value={orderID}
			on:input={handleSearch}
		/>
		<select name="" id="" bind:value={attribution}>
			<option value="all">All</option>
			<option value="Other">Other</option>
			<option value="Found Through Shopbox">Found Through Shopbox</option>
			<option value="Viewed Through Shopbox">Viewed Through Shopbox</option>
		</select>
		<DatePicker bind:isOpen bind:startDate bind:endDate isRange showPresets>
			<div class="date-field" on:click={toggleDatePicker} class:open={isOpen}>
				<i class="icon-calendar" />
				<div class="date">
					{formattedStartDate} - {formattedEndDate}
				</div>
				<span on:click={onClearDates}>
					<i class="os-icon-x" />
				</span>
			</div>
		</DatePicker>
		<button on:click={exportToCSV}>Export to CSV</button>
	</nav>
	<div class="table-container" on:scroll={handleScroll}>
		<table>
			<thead>
				{#each ['attribution', 'date', 'order_id', 'sku', 'total'] as header}
					<th>
						{header
							.split('_')
							.map((word) => word.charAt(0).toUpperCase() + word.slice(1))
							.join(' ')}
					</th>
				{/each}
			</thead>
			<tbody>
				{#if tableData?.length > 0}
					{#each tableData as row}
						<tr>
							{#each Object.values(row) as cell}
								<td>{cell}</td>
							{/each}
						</tr>
					{/each}
				{/if}
			</tbody>
		</table>
		{#if loading}
			<p><span class="loader" /></p>
		{:else if moreData && tableData.length > 0}
			<button on:click={loadMoreData}>Load More</button>
		{:else}
			<p>No more data to load.</p>
		{/if}
	</div>
</section>

<style>
	.table-container nav {
		margin-top: 1em;
	}

	.table-container {
		height: 500px;
		overflow-y: scroll;
	}
	table {
		width: 100%;
	}
	.table-container th {
		text-align: left;
		padding: 5px 10px;
		cursor: pointer;
	}
	.table-container td {
		max-width: 80px;
		padding: 5px 10px;
		overflow: hidden;
		text-overflow: ellipsis;
		white-space: nowrap;
		border-bottom: 1px solid #ddd;
	}
	input[type='text'] {
		width: 100%;
		padding: 4px;

		border: 1px solid #999;
		border-radius: 4px;
	}
	button {
		white-space: nowrap;
		margin: 0;
	}
</style>
